import React from 'react';
import { Eye, EyeOff, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';

type AdjustmentType = 'bezug' | 'abzug';

/** Gemeinsames Spaltenraster für Kopfzeile und Zeilen: Griff · Kategorie · Typ · Lohnart · Sichtbar · Löschen. */
const ROW_GRID =
  'grid grid-cols-[1.5rem_minmax(0,1fr)_8.5rem_5.5rem_2rem_2rem] items-center gap-x-3';

type CategoryRow = {
  category_key: string;
  label: string;
  type: AdjustmentType;
  lohnart_code: string;
  is_custom: boolean;
  hidden: boolean;
  sort_order: number;
};

function parseRows(data: unknown): CategoryRow[] {
  if (!Array.isArray(data)) return [];
  return data.map((raw, idx) => {
    const r = raw as Partial<CategoryRow>;
    return {
      category_key: String(r.category_key ?? ''),
      label: String(r.label ?? ''),
      type: r.type === 'bezug' ? 'bezug' : 'abzug',
      lohnart_code: String(r.lohnart_code ?? ''),
      is_custom: Boolean(r.is_custom),
      hidden: Boolean(r.hidden),
      sort_order: idx,
    };
  });
}

function newCustomKey(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `custom_${Date.now().toString(36)}_${rand}`;
}

function TypeToggle({
  value,
  disabled,
  onChange,
}: {
  value: AdjustmentType;
  disabled?: boolean;
  onChange: (next: AdjustmentType) => void;
}) {
  const btn = (t: AdjustmentType, label: string) => {
    const active = value === t;
    const color = t === 'bezug' ? 'text-emerald-700' : 'text-red-600';
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(t)}
        aria-pressed={active}
        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
          active ? `bg-app-surface shadow-sm ${color}` : 'text-app-text-subtle hover:text-app-text'
        } disabled:cursor-default`}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="inline-flex shrink-0 rounded-md bg-app-surface-muted p-0.5">
      {btn('bezug', 'Bezug +')}
      {btn('abzug', 'Abzug −')}
    </div>
  );
}

function SortableCategoryRow({
  row,
  onUpdate,
  onRemove,
}: {
  row: CategoryRow;
  onUpdate: (patch: Partial<CategoryRow>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.category_key,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.92 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`${ROW_GRID} py-1.5 border-b border-app-border last:border-0 bg-app-surface`}
    >
      <button
        type="button"
        className="p-1 rounded-md text-slate-400 hover:text-app-text-muted hover:bg-app-hover cursor-grab active:cursor-grabbing touch-none"
        title="Ziehen zum Sortieren"
        aria-label="Reihenfolge per Ziehen ändern"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" strokeWidth={2} />
      </button>
      <div className="flex min-w-0 items-center gap-2">
        {row.is_custom ? (
          <input
            type="text"
            value={row.label}
            maxLength={120}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className={`${SETTINGS_INPUT_CLASS} h-8 ${row.hidden ? 'text-app-text-subtle' : ''}`}
            placeholder="Bezeichnung"
            aria-label="Bezeichnung"
          />
        ) : (
          <span
            className={`truncate text-sm ${row.hidden ? 'text-app-text-subtle line-through' : 'text-app-text'}`}
            title={row.label}
          >
            {row.label}
          </span>
        )}
        {row.hidden ? (
          <span className="shrink-0 rounded bg-app-surface-muted px-1.5 py-0.5 text-[11px] text-app-text-subtle">
            ausgeblendet
          </span>
        ) : null}
      </div>
      {row.is_custom ? (
        <TypeToggle value={row.type} onChange={(type) => onUpdate({ type })} />
      ) : (
        <span
          className={`text-xs font-medium ${row.type === 'bezug' ? 'text-emerald-700' : 'text-red-600'}`}
        >
          {row.type === 'bezug' ? 'Bezug +' : 'Abzug −'}
        </span>
      )}
      <div>
        <label className="sr-only" htmlFor={`lohnart-${row.category_key}`}>
          Lohnart für {row.label}
        </label>
        <input
          id={`lohnart-${row.category_key}`}
          type="text"
          inputMode="numeric"
          value={row.lohnart_code}
          maxLength={20}
          onChange={(e) => onUpdate({ lohnart_code: e.target.value })}
          placeholder="—"
          className={`${SETTINGS_INPUT_CLASS} h-8 px-2 font-mono`}
        />
      </div>
      <button
        type="button"
        onClick={() => onUpdate({ hidden: !row.hidden })}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover"
        title={row.hidden ? 'Einblenden' : 'Ausblenden'}
        aria-label={row.hidden ? 'Ausgeblendet' : 'Sichtbar'}
        aria-pressed={!row.hidden}
      >
        {row.hidden ? (
          <EyeOff className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
        ) : (
          <Eye className="w-4 h-4" strokeWidth={1.75} />
        )}
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={!row.is_custom}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 ${
          row.is_custom ? '' : 'invisible'
        }`}
        title={
          row.is_custom
            ? 'Kategorie entfernen'
            : 'Standardkategorie kann nicht entfernt, nur ausgeblendet werden'
        }
        aria-label="Kategorie entfernen"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

export function PayrollAdjustmentLohnartenSettingsSection() {
  const [rows, setRows] = React.useState<CategoryRow[]>([]);
  const [baseline, setBaseline] = React.useState<CategoryRow[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [catalogReady, setCatalogReady] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [labelDraft, setLabelDraft] = React.useState('');
  const [typeDraft, setTypeDraft] = React.useState<AdjustmentType>('abzug');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyRows = React.useCallback((next: CategoryRow[]) => {
    const normalized = next.map((row, idx) => ({ ...row, sort_order: idx }));
    setRows(normalized);
    setBaseline(normalized);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/payroll-adjustment-lohnarten', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Laden fehlgeschlagen'))))
      .then((data) => {
        if (cancelled) return;
        const parsed = parseRows(data);
        if (parsed.length === 0) throw new Error('Leerer Katalog');
        applyRows(parsed);
        setCatalogReady(true);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Kategorien konnten nicht geladen werden.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applyRows]);

  const isDirty = catalogReady && !settingsValuesEqual(rows, baseline);
  const hasEmptyLabel = rows.some((r) => r.is_custom && !r.label.trim());

  const save = async () => {
    if (!catalogReady) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/payroll-adjustment-lohnarten', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          rows.map((r, idx) => ({
            category_key: r.category_key,
            label: r.label.trim(),
            type: r.type,
            lohnart_code: r.lohnart_code.trim(),
            hidden: r.hidden,
            sort_order: idx,
          })),
        ),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError((data as { error?: string } | null)?.error || 'Speichern fehlgeschlagen.');
        return;
      }
      applyRows(parseRows(data));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const update = (key: string, patch: Partial<CategoryRow>) => {
    setRows((prev) => prev.map((row) => (row.category_key === key ? { ...row, ...patch } : row)));
    setError(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const from = prev.findIndex((row) => row.category_key === String(active.id));
      const to = prev.findIndex((row) => row.category_key === String(over.id));
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to).map((item, idx) => ({ ...item, sort_order: idx }));
    });
    setError(null);
  };

  const add = () => {
    const label = labelDraft.trim();
    if (!label || !catalogReady) return;
    if (rows.some((r) => r.label.trim().toLocaleLowerCase('de') === label.toLocaleLowerCase('de'))) {
      setError(`Kategorie „${label}“ gibt es bereits.`);
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        category_key: newCustomKey(),
        label,
        type: typeDraft,
        lohnart_code: '',
        is_custom: true,
        hidden: false,
        sort_order: prev.length,
      },
    ]);
    setLabelDraft('');
    setError(null);
  };

  const remove = (key: string) => {
    setRows((prev) => {
      const row = prev.find((item) => item.category_key === key);
      if (!row || !row.is_custom) return prev;
      return prev
        .filter((item) => item.category_key !== key)
        .map((item, idx) => ({ ...item, sort_order: idx }));
    });
    setError(null);
  };

  return (
    <SettingsCard
      title="Be- und Abzüge"
      description="Kategorien für Bezüge und Abzüge in der Lohnbuchhaltung. Die Lohnart wird im Lohnbuchungen-Export verwendet. Ausgeblendete Kategorien stehen für neue Buchungen nicht mehr zur Auswahl; bestehende Buchungen und Daueraufträge laufen weiter. Eigene Kategorien lassen sich nur löschen, solange es keine Buchungen dazu gibt."
    >
      {!loaded ? (
        <InlineLoadingState label="Lade…" className="justify-start" />
      ) : (
        <div className="space-y-4">
          <div className={`${ROW_GRID} pb-1.5 border-b border-app-border`}>
            <span />
            <span className={SETTINGS_TABLE_HEADER}>Kategorie</span>
            <span className={SETTINGS_TABLE_HEADER}>Typ</span>
            <span className={SETTINGS_TABLE_HEADER}>Lohnart</span>
            <span className={`col-span-2 text-center ${SETTINGS_TABLE_HEADER}`}>Sichtbar</span>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={rows.map((row) => row.category_key)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-0">
                {rows.map((row) => (
                  <SortableCategoryRow
                    key={row.category_key}
                    row={row}
                    onUpdate={(patch) => update(row.category_key, patch)}
                    onRemove={() => remove(row.category_key)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <div className="flex min-w-[12rem] flex-1 flex-wrap items-center gap-2">
              <input
                type="text"
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  add();
                }}
                maxLength={120}
                placeholder="Neue Kategorie"
                className={`min-w-0 flex-1 ${SETTINGS_INPUT_CLASS}`}
                disabled={!catalogReady}
              />
              <TypeToggle value={typeDraft} disabled={!catalogReady} onChange={setTypeDraft} />
              <button
                type="button"
                onClick={add}
                disabled={!catalogReady || !labelDraft.trim()}
                className={`${SETTINGS_BTN_OUTLINE} border-dashed border-pipedrive-blue text-pipedrive-blue hover:bg-blue-50 disabled:opacity-40`}
              >
                <Plus className="w-3.5 h-3.5" />
                Hinzufügen
              </button>
            </div>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!isDirty || saving || hasEmptyLabel}
              className={
                isDirty
                  ? SETTINGS_BTN_PRIMARY
                  : 'inline-flex h-9 shrink-0 items-center px-3.5 text-sm font-medium rounded-md text-app-text-subtle bg-app-surface-muted disabled:opacity-40'
              }
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
            <SettingsCardSavedStatus saved={saved} />
          </div>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
      )}
    </SettingsCard>
  );
}
